import React, { useState, useEffect, useRef } from 'react';
import { Shield, TrendingUp, Award } from 'lucide-react';
import { stats } from '../data/mockData';

const StatsSection = () => {
  const icons = [Shield, TrendingUp, Award];
  const [counters, setCounters] = useState([0, 0, 0]);
  const [hasStarted, setHasStarted] = useState(false);
  const sectionRef = useRef(null);

  // Extract numbers from stats
  const getNumber = (statNumber) => {
    const match = statNumber.match(/[\d,]+/);
    return match ? parseInt(match[0].replace(/,/g, '')) : 0;
  };

  const targetNumbers = stats.map(stat => getNumber(stat.number));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;

    const duration = 2000; // 2 seconds
    const steps = 60;
    const interval = duration / steps;

    const timers = targetNumbers.map((target, index) => {
      let current = 0;
      const increment = target / steps;

      return setInterval(() => {
        current += increment;
        if (current >= target) {
          current = target;
          clearInterval(timers[index]);
        }
        setCounters(prev => {
          const newCounters = [...prev];
          newCounters[index] = Math.floor(current);
          return newCounters;
        });
      }, interval);
    });

    return () => timers.forEach(timer => clearInterval(timer));
  }, [hasStarted]);

  const formatNumber = (num, originalStat) => {
    // Format with commas
    const formatted = num.toLocaleString();
    // Add suffix if original had one
    if (originalStat.includes('+')) return formatted + '+';
    if (originalStat.includes('%')) return formatted + '%';
    return formatted;
  };

  return (
    <section ref={sectionRef} className="py-20 bg-[#2C3E50]">
      <div className="container mx-auto px-4 max-w-[1320px]">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-bold text-white">
            Proven Track Record
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Numbers that speak to our commitment to excellence and client success
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {stats.map((stat, index) => {
            const Icon = icons[index];
            return (
              <div 
                key={index}
                className="relative group"
              >
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/10 hover:border-[#C0392B]/50 transition-all duration-500 hover:bg-white/10 card-lift group">
                  <div className="flex flex-col items-center text-center space-y-4">
                    {/* Icon */}
                    <div className="w-16 h-16 bg-[#C0392B]/20 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 icon-pulse">
                      <Icon className="w-8 h-8 text-[#C0392B] group-hover:scale-125 transition-transform duration-500" />
                    </div>

                    {/* Number with Counter Animation */}
                    <div className="text-5xl md:text-6xl font-bold text-white group-hover:scale-110 transition-transform duration-300">
                      {formatNumber(counters[index], stat.number)}
                    </div>

                    {/* Label */}
                    <div className="text-xl font-semibold text-[#C0392B]">
                      {stat.label}
                    </div>

                    {/* Description */}
                    <p className="text-gray-300 leading-relaxed group-hover:text-white transition-colors duration-300">
                      {stat.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;